import React, { useCallback, useEffect, useRef, useState } from "react";
import constate from "constate";
import authApi from "../client-api/auth";
import { useTimer } from "react-use-precision-timer";
import {
  CircuitProgram,
  WeightProgram,
  BasicProgram,
  HiLuxProgram,
} from "../types";
import { getProgram } from "../client-api/program";
import dayjs from "dayjs";
import EventEmitter from "eventemitter3";
import { getRowing } from "../client-api/rowing";
import { getFitsStore } from "../client-api/fits";
import toast from "react-hot-toast";
import { cleanupAudioCache } from "../hooks/useAudio";

const preloadVideoList: {
  video: string;
  url: string;
}[] = [];
async function preloadVideo(src: string) {
  const res = await fetch(src);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function useAuth() {
  const emitter = useRef<EventEmitter>(new EventEmitter());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [did, setDid] = useState<"0" | "L1" | "L6" | "R1" | "R6" | "1">();
  const [row, setRow] = useState<"300m" | "600m" | "1200m">();
  const [selectedUserIdsByRow, setSelectedUserIdsByRow] = React.useState<
    number[][]
  >([[], [], [], []]);
  const [rowProgressData, setRowProgressData] = useState<{
    L1: number;
    L6: number;
    R1: number;
    R6: number;
  }>({
    L1: 0,
    L6: 0,
    R1: 0,
    R6: 0,
  });
  const firstProgressData = useRef<{
    L1: number;
    L6: number;
    R1: number;
    R6: number;
  }>({
    L1: 0,
    L6: 0,
    R1: 0,
    R6: 0,
  });

  const [rowConnected, setRowConnected] = useState<{
    L1: boolean;
    L6: boolean;
    R1: boolean;
    R6: boolean;
  }>({
    L1: false,
    L6: false,
    R1: false,
    R6: false,
  });
  // const socket = useRef<WebSocket>(
  //   new WebSocket(process.env.REACT_APP_WEB_SOCKET_URL + "/websocket")
  // );
  const socket = useRef<WebSocket>();
  const rowerSocket = useRef<WebSocket>();
  const rowerIntervalRef = useRef<NodeJS.Timeout>();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 10;

  const [step, setStep] = useState(0);
  const [weightProgram, setWeightProgram] = useState<WeightProgram>();
  const [circuitProgram, setCircuitProgram] = useState<CircuitProgram>();
  const [basicProgram, setBasicProgram] = useState<BasicProgram>();
  const [hiluxProgram, setHiluxProgram] = useState<HiLuxProgram>();
  const [hiluxDifficulties, setHiluxDifficulties] = useState<
    (null | "easy" | "normal" | "hard")[]
  >([]);
  const [hiluxSessionId, setHiluxSessionId] = useState<number | undefined>();
  const [index, setIndex] = useState<number | undefined>();
  const [classId, setClassId] = useState<number | undefined>();
  // 코인 운영 지점이면 수업 화면에 실시간 심박·예상 포인트를 보여준다.
  const [isCoinEnabled, setIsCoinEnabled] = useState(false);
  // 지점 운영설정 '심박수'. 꺼진 지점은 수업 실시간 심박 화면을 띄우지 않는다.
  const [isHeartRateEnabled, setIsHeartRateEnabled] = useState(false);
  const startTime = useRef<number>(0);
  const isExeOpen = useRef(false);

  const timer = useTimer({
    fireOnStart: false,
    startImmediately: false,
  });

  const sendRowDisconnect = () => {
    rowerSocket.current?.send(
      JSON.stringify({
        type: "disconnect",
      }),
    );
  };

  useEffect(() => {
    if (step === 0 && (did === "L1" || did === "R1") && rowerSocket.current) {
      if (window.electronAPI) {
        window.electronAPI.closeExe();
        isExeOpen.current = false;
      }
      rowerSocket.current.close();
      rowerSocket.current = undefined;
    }
  }, [did, step]);

  const connectRowSocket = useCallback(() => {
    if (rowerSocket.current) {
      rowerSocket.current.addEventListener("close", (event) => {
        console.log("rowerSocket close");
      });
      rowerSocket.current.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);
          let bleDid = "";
          if (did === "L1" && data.client === 0) {
            bleDid = "L1";
          } else if (did === "L1" && data.client === 1) {
            bleDid = "L6";
          } else if (did === "R1" && data.client === 0) {
            bleDid = "R1";
          } else if (did === "R1" && data.client === 1) {
            bleDid = "R6";
          }
          switch (data.type) {
            case "connected":
              sendMessage(
                JSON.stringify({
                  type: "ROWER_CONNECTED",
                  did: bleDid,
                }),
              );
              break;
            case "disconnected":
              sendMessage(
                JSON.stringify({
                  type: "ROWER_DISCONNECTED",
                  did: bleDid,
                }),
              );
              break;
            case "error":
              toast("머신 연결에 실패했습니다. " + data.message);
              break;
            case "distance":
              sendMessage(
                JSON.stringify({
                  type: "DISTANCE",
                  distance: data.distance,
                  did: bleDid,
                }),
              );
              break;
          }
        } catch (e) {
          toast("rowerSocket message error" + e + typeof e);
          console.log(e);
        }
      });
    }
  }, [did]);

  useEffect(() => {
    let isCleaningUp = false;
    let openHandler: ((event: Event) => void) | null = null;
    let messageHandler: ((event: MessageEvent) => void) | null = null;
    let closeHandler: ((event: Event) => void) | null = null;

    const connectSocket = () => {
      if (isCleaningUp) return;

      // Clean up old socket if exists
      if (socket.current) {
        if (openHandler)
          socket.current.removeEventListener("open", openHandler);
        if (messageHandler)
          socket.current.removeEventListener("message", messageHandler);
        if (closeHandler)
          socket.current.removeEventListener("close", closeHandler);
        socket.current.close();
      }

      socket.current = new WebSocket(
        process.env.REACT_APP_WEB_SOCKET_URL + "/websocket",
      );

      openHandler = (event) => {
        reconnectAttemptsRef.current = 0;
        if (localStorage.getItem("token")) {
          socket.current?.send(
            JSON.stringify({
              type: "CREATE_SESSION",
              data: localStorage.getItem("token"),
            }),
          );
        }
      };

      messageHandler = (event) => {
        try {
          const data = JSON.parse(JSON.parse(event.data));
          if (data.type === "START_PROGRAM") {
            startTime.current = 0;
            setLoading(true);
            setStep(0);
            setWeightProgram(undefined);
            setCircuitProgram(undefined);
            setBasicProgram(undefined);
            setHiluxProgram(undefined);
            setHiluxDifficulties([]);
            setHiluxSessionId(undefined);
            setRow(data.rowType);
            setClassId(data.selectedClassId ?? undefined);
            setSelectedUserIdsByRow([[], [], [], []]);
            setRowProgressData({
              L1: 0,
              L6: 0,
              R1: 0,
              R6: 0,
            });
            firstProgressData.current = {
              L1: 0,
              L6: 0,
              R1: 0,
              R6: 0,
            };
            setRowConnected({
              L1: false,
              L6: false,
              R1: false,
              R6: false,
            });

            // Clean up audio cache when starting new program
            // cleanupAudioCache();
            getProgram(data.programId).then(async (program) => {
              if (program.programType === "WEIGHT") {
                const weightProgram = program as WeightProgram;
                const videoList = [
                  ...weightProgram.coolDownMovements.map(
                    (movement) => movement.movement.video,
                  ),
                  ...weightProgram.warmUpMovements.map(
                    (movement) => movement.video,
                  ),
                  ...weightProgram.firstHalfMovements.map(
                    (movement) => movement.movement.video,
                  ),
                  ...weightProgram.secondHalfMovements.map(
                    (movement) => movement.movement.video,
                  ),
                ];

                const newVideoList = videoList.reduce(
                  (acc, cur) =>
                    acc.includes(cur) ||
                    preloadVideoList.find((video) => video.video === cur)
                      ? acc
                      : [...acc, cur],
                  [] as string[],
                );

                const _preloadVideoList = await Promise.all(
                  newVideoList.map(async (video) => {
                    const url = await preloadVideo(video);
                    return { video, url };
                  }),
                );
                preloadVideoList.push(..._preloadVideoList);
                weightProgram.coolDownMovements.map(
                  (movement) =>
                    (movement.movement.video = preloadVideoList.find(
                      (video) => video.video === movement.movement.video,
                    )!.url),
                );
                weightProgram.warmUpMovements.map(
                  (movement) =>
                    (movement.video = preloadVideoList.find(
                      (video) => video.video === movement.video,
                    )!.url),
                );
                weightProgram.firstHalfMovements.map(
                  (movement) =>
                    (movement.movement.video = preloadVideoList.find(
                      (video) => video.video === movement.movement.video,
                    )!.url),
                );
                weightProgram.secondHalfMovements.map(
                  (movement) =>
                    (movement.movement.video = preloadVideoList.find(
                      (video) => video.video === movement.movement.video,
                    )!.url),
                );

                setLoading(false);
                setWeightProgram(weightProgram);
              }
              if (program.programType === "BASIC") {
                const basicProgram = program as any as BasicProgram;
                const didStorage = localStorage.getItem("did");
                if (didStorage !== "0") {
                  const videoList = [
                    ...basicProgram.coolDownMovements.map(
                      (movement) => movement.movement.video,
                    ),
                    ...basicProgram.warmUpMovements.map(
                      (movement) => movement.movement.video,
                    ),
                    ...basicProgram.mainExers.map(
                      (movement) => movement.movement.video,
                    ),
                  ];

                  const newVideoList = videoList.reduce(
                    (acc, cur) =>
                      acc.includes(cur) ||
                      preloadVideoList.find((video) => video.video === cur)
                        ? acc
                        : [...acc, cur],
                    [] as string[],
                  );

                  const _preloadVideoList = await Promise.all(
                    newVideoList.map(async (video) => {
                      const url = await preloadVideo(video);
                      return { video, url };
                    }),
                  );
                  preloadVideoList.push(..._preloadVideoList);
                  basicProgram.coolDownMovements.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video,
                      )!.url),
                  );
                  basicProgram.warmUpMovements.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video,
                      )!.url),
                  );
                  basicProgram.mainExers.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video,
                      )!.url),
                  );
                }

                setLoading(false);
                setBasicProgram(basicProgram);
              }
              if (
                program.programType === "CIRCUIT" ||
                program.programType === "BOOSTER"
              ) {
                const circuitProgram = program as CircuitProgram;
                if (circuitProgram.programType === "BOOSTER") {
                  circuitProgram.areaAMovements = (
                    circuitProgram.areaAMovements as any[]
                  ).map((item) =>
                    item && item.movement
                      ? { ...item.movement, text: item.text }
                      : item,
                  );
                  circuitProgram.areaBMovements = (
                    circuitProgram.areaBMovements as any[]
                  ).map((item) =>
                    item && item.movement
                      ? { ...item.movement, text: item.text }
                      : item,
                  );
                }
                let videoList = [
                  ...circuitProgram.coolDownMovements.map(
                    (movement) => movement.movement.video,
                  ),
                  ...circuitProgram.warmUpMovements.map(
                    (movement) => movement.video,
                  ),
                  ...circuitProgram.areaAMovements.map(
                    (movement) => movement.video,
                  ),
                  ...circuitProgram.areaBMovements.map(
                    (movement) => movement.video,
                  ),
                ];

                const newVideoList = videoList.reduce(
                  (acc, cur) =>
                    acc.includes(cur) ||
                    preloadVideoList.find((video) => video.video === cur)
                      ? acc
                      : [...acc, cur],
                  [] as string[],
                );

                const _preloadVideoList = await Promise.all(
                  newVideoList.map(async (video) => {
                    const url = await preloadVideo(video);
                    return { video, url };
                  }),
                );
                preloadVideoList.push(..._preloadVideoList);

                circuitProgram.coolDownMovements.map(
                  (movement) =>
                    (movement.movement.video = preloadVideoList.find(
                      (video) => video.video === movement.movement.video,
                    )!.url),
                );
                circuitProgram.warmUpMovements.map(
                  (movement) =>
                    (movement.video = preloadVideoList.find(
                      (video) => video.video === movement.video,
                    )!.url),
                );
                circuitProgram.areaAMovements.map(
                  (movement) =>
                    (movement.video = preloadVideoList.find(
                      (video) => video.video === movement.video,
                    )!.url),
                );
                circuitProgram.areaBMovements.map(
                  (movement) =>
                    (movement.video = preloadVideoList.find(
                      (video) => video.video === movement.video,
                    )!.url),
                );

                setLoading(false);
                setCircuitProgram(circuitProgram);
              }
              if (program.programType === "HILUX") {
                const hilux = program as unknown as HiLuxProgram;
                setLoading(false);
                setHiluxProgram(hilux);
                setHiluxDifficulties(
                  Array.from({ length: 8 }, () => null as null),
                );
                setSelectedUserIdsByRow(
                  Array.from({ length: 8 }, () => [] as number[]),
                );
              }
              setStep(1);
            });
          }
          if (data.type === "START_TIMER") {
            const time = data.startTime;
            timer.start();
            setStep(2);
          }
          if (data.type === "SET_STEP") {
            const time = data.startTime;
            setStep(data.step);
            startTime.current = time;
          }
          if (data.type === "START_COUNTDOWN") {
            emitter.current.emit("resume", data.startTime);
          }
          if (data.type === "STOP_COUNTDOWN") {
            emitter.current.emit("pause", data.startTime);
          }
          if (data.type === "CURRENT_INDEX") {
            console.log("CURRENT_INDEX", data.index);
            setIndex(data.index);
          }
          if (data.type === "DISTANCE") {
            const did: "L1" | "L6" | "R1" | "R6" = data.did;
            let distance = data.distance;
            if (firstProgressData.current[did] === 0) {
              firstProgressData.current[did] = data.distance;
            }
            if (distance === 0) {
              firstProgressData.current[did] = 0;
            }
            distance = data.distance - firstProgressData.current[did];
            setRowProgressData((prev) => ({
              ...prev,
              [data.did]: Math.max(distance, 0),
            }));
          }
          if (data.type === "ROWER_CONNECTED") {
            const validDids = ["L1", "R1", "L6", "R6"];

            if (validDids.includes(data.did)) {
              setRowConnected((prev) => ({
                ...prev,
                [data.did]: true,
              }));
            }
          }
          if (data.type === "ROWER_DISCONNECTED") {
            const validDids = ["L1", "R1", "L6", "R6"];

            if (validDids.includes(data.did)) {
              setRowConnected((prev) => ({
                ...prev,
                [data.did]: false,
              }));
            }
          }
          if (data.type === "SELECT_USER") {
            handleSelectedUserIdsByRow(data.userId, data.row);
          }
          if (data.type === "SET_HILUX_DIFFICULTY") {
            setHiluxDifficulties((prev) => {
              const next = [...prev];
              next[data.stIdx] = data.difficulty;
              return next;
            });
          }
          if (data.type === "SET_HILUX_SESSION") {
            setHiluxSessionId(data.sessionId);
          }
        } catch (e) {
          console.log(e);
        }
      };

      closeHandler = (event) => {
        console.log("WebSocket closed");

        if (
          !isCleaningUp &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptsRef.current++;
          const backoffDelay = Math.min(
            300 * Math.pow(2, reconnectAttemptsRef.current),
            30000,
          );

          console.log(
            `Reconnecting attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${backoffDelay}ms`,
          );

          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }

          reconnectTimeoutRef.current = setTimeout(() => {
            connectSocket();
          }, backoffDelay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error("Max reconnection attempts reached");
        }
      };

      socket.current.addEventListener("open", openHandler);
      socket.current.addEventListener("message", messageHandler);
      socket.current.addEventListener("close", closeHandler);
    };

    connectSocket();

    return () => {
      isCleaningUp = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (socket.current) {
        if (openHandler)
          socket.current.removeEventListener("open", openHandler);
        if (messageHandler)
          socket.current.removeEventListener("message", messageHandler);
        if (closeHandler)
          socket.current.removeEventListener("close", closeHandler);
        socket.current.close();
      }

      emitter.current.removeAllListeners();

      // Clean up rower interval
      if (rowerIntervalRef.current) {
        clearInterval(rowerIntervalRef.current);
        rowerIntervalRef.current = undefined;
      }

      // Clean up rower socket
      if (rowerSocket.current) {
        rowerSocket.current.close();
        rowerSocket.current = undefined;
      }
    };
  }, []);

  const login = async (username: string, password: string, did: string) => {
    if (
      did !== "0" &&
      did !== "L1" &&
      did !== "L6" &&
      did !== "R1" &&
      did !== "R6" &&
      did !== "1"
    ) {
      toast("DID를 확인해주세요.");
      return;
    }
    try {
      const data = await authApi.login(username, password, did);
      localStorage.setItem("token", data.tokens.accessToken);
      localStorage.setItem("refreshToken", data.tokens.refreshToken);
      localStorage.setItem("name", data.name);
      localStorage.setItem("did", did);
      localStorage.setItem("username", username);
      setDid(did as any);
      setIsAuthenticated(true);
      setSelectedUserIdsByRow([[], [], [], []]);
      setStep(0);
      setWeightProgram(undefined);
      setCircuitProgram(undefined);
      setBasicProgram(undefined);
      setHiluxProgram(undefined);
      setHiluxDifficulties([]);
      setRow(undefined);
      setRowProgressData({
        L1: 0,
        L6: 0,
        R1: 0,
        R6: 0,
      });
      firstProgressData.current = {
        L1: 0,
        L6: 0,
        R1: 0,
        R6: 0,
      };
      setRowConnected({
        L1: false,
        L6: false,
        R1: false,
        R6: false,
      });
      socket.current?.send(
        JSON.stringify({
          type: "CREATE_SESSION",
          data: data.tokens.accessToken,
        }),
      );
    } catch (e) {
      toast("아이디와 비밀번호를 확인해주세요.");
    }
  };

  const logout = async () => {
    await authApi.logout();
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("did");

    // Clean up audio cache on logout
    // cleanupAudioCache();

    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    const data = await authApi.refreshToken();
    localStorage.setItem("token", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    setIsAuthenticated(true);
    setLoading(false);
    setDid(localStorage.getItem("did") as any);
    socket.current?.send(
      JSON.stringify({
        type: "CREATE_SESSION",
        data: localStorage.getItem("token"),
      }),
    );
  };

  useEffect(() => {
    if (localStorage.getItem("token")) {
      (async () => {
        try {
          await refreshToken();
        } catch {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!isAuthenticated || !username) {
      setIsCoinEnabled(false);
      setIsHeartRateEnabled(false);
      return;
    }
    getFitsStore(username)
      .then((store) => {
        setIsCoinEnabled(store.isCoinEnabled);
        // 서버가 아직 이 필드를 내려주지 않으면(구버전) 꺼진 것으로 본다.
        setIsHeartRateEnabled(store.isHeartRateEnabled === true);
      })
      .catch(() => {
        setIsCoinEnabled(false);
        setIsHeartRateEnabled(false);
      });
  }, [isAuthenticated]);

  const sendMessage = (message: string) => {
    socket.current?.send(
      JSON.stringify({
        type: "SEND_MESSAGE",
        data: message,
      }),
    );
  };
  const startProgram = (
    programId: number,
    rowType: "300m" | "600m" | "1200m" | undefined,
    selectedClassId: number | null,
  ) => {
    sendMessage(
      JSON.stringify({
        type: "START_PROGRAM",
        programId: programId,
        rowType: rowType,
        selectedClassId: selectedClassId,
      }),
    );
  };
  const sendStartTimer = () => {
    sendMessage(
      JSON.stringify({
        type: "START_TIMER",
        startTime: Date.now() + 500,
      }),
    );
  };

  const sendSetStep = (step: number) => {
    sendMessage(
      JSON.stringify({
        type: "SET_STEP",
        step: step,
        startTime: Date.now() + 500,
      }),
    );
  };

  const sendStartCountDown = () => {
    sendMessage(
      JSON.stringify({
        type: "START_COUNTDOWN",
        startTime: Date.now() + 500,
      }),
    );
  };

  const sendStopCountDown = () => {
    sendMessage(
      JSON.stringify({
        type: "STOP_COUNTDOWN",
        startTime: Date.now() + 500,
      }),
    );
  };

  const sendCurrentIndex = (index: number | undefined) => {
    sendMessage(
      JSON.stringify({
        type: "CURRENT_INDEX",
        index: index,
      }),
    );
  };

  const sendRowConnect = useCallback(async () => {
    if (rowerSocket.current || isExeOpen.current === true) {
      return;
    }
    if (window.electronAPI && (did === "L1" || did === "R1") && row) {
      isExeOpen.current = true;
      window.electronAPI.openExe();
    }
    let opend = false;
    const openRowSocket = async () => {
      opend = true;
      console.log("rowerSocket open");
      const data = await getRowing();
      rowerSocket.current?.send(
        JSON.stringify({
          type: "connect",
          pins:
            did === "L1"
              ? data.rowingMachineIds.slice(0, 2)
              : data.rowingMachineIds.slice(2, 4),
        }),
      );
    };
    if (did === "L1" || did === "R1") {
      // Clear any existing interval before creating new one
      if (rowerIntervalRef.current) {
        clearInterval(rowerIntervalRef.current);
        rowerIntervalRef.current = undefined;
      }

      await new Promise((resolve) => {
        const intervalFunc = () => {
          if (opend) {
            if (rowerIntervalRef.current) {
              clearInterval(rowerIntervalRef.current);
              rowerIntervalRef.current = undefined;
            }
            resolve(undefined);
          } else {
            rowerSocket.current?.removeEventListener("open", openRowSocket);
            rowerSocket.current?.close();
            rowerSocket.current = undefined;
            rowerSocket.current = new WebSocket("ws://localhost:8765");
            connectRowSocket();
            rowerSocket.current?.addEventListener("open", openRowSocket);
          }
        };
        rowerIntervalRef.current = setInterval(intervalFunc, 5000);
        intervalFunc();
      });
    }
  }, [connectRowSocket, did, row]);

  const handleSelectedUserIdsByRow = (userId: number, row: number) => {
    setSelectedUserIdsByRow((prev) => {
      if (prev[row].includes(userId)) {
        return prev.map((ids, index) =>
          index === row ? ids.filter((id) => id !== userId) : ids,
        );
      } else {
        return prev.map((ids, index) =>
          index === row ? [...ids, userId] : ids,
        );
      }
    });
  };

  const sendHandleSelectedUserIdsByRow = (userId: number, row: number) => {
    sendMessage(
      JSON.stringify({
        type: "SELECT_USER",
        userId: userId,
        row: row,
      }),
    );
  };

  const sendSetHiluxDifficulty = (
    stIdx: number,
    difficulty: "easy" | "normal" | "hard",
  ) => {
    sendMessage(
      JSON.stringify({
        type: "SET_HILUX_DIFFICULTY",
        stIdx,
        difficulty,
      }),
    );
  };

  const sendSetHiluxSession = (sessionId: number) => {
    sendMessage(
      JSON.stringify({
        type: "SET_HILUX_SESSION",
        sessionId,
      }),
    );
  };

  return {
    isAuthenticated,
    login,
    logout,
    refreshToken,
    loading,
    did,
    socket,
    step,
    setStep,
    timer,
    weightProgram,
    circuitProgram,
    basicProgram,
    hiluxProgram,
    hiluxDifficulties,
    setHiluxDifficulties,
    startProgram,
    sendStartTimer,
    sendSetStep,
    startTime,
    emitter,
    sendStartCountDown,
    sendStopCountDown,
    row,
    rowConnected,
    rowProgressData,
    sendRowConnect,
    sendRowDisconnect,
    classId,
    isCoinEnabled,
    isHeartRateEnabled,
    selectedUserIdsByRow,
    sendHandleSelectedUserIdsByRow,
    sendSetHiluxDifficulty,
    sendSetHiluxSession,
    hiluxSessionId,
    setHiluxSessionId,
    index,
    sendCurrentIndex,
  };
}

// 2️⃣ Wrap your hook with the constate factory
const [AuthProvider, useAuthContext] = constate(useAuth);

export { AuthProvider, useAuthContext };
